import dayjs from 'dayjs';

/**
 * Calculates the lease duration in months between two dates.
 * Uses Math.ceil to round up partial months.
 *
 * @param leaseStart - The lease start date
 * @param leaseEnd - The lease end date
 * @returns The number of months between the dates, rounded up. Returns 0 if either date is invalid.
 */
export const calculateLeaseDurationMonths = (leaseStart?: Date, leaseEnd?: Date): number => {
  if (!leaseStart || !leaseEnd) {
    return 0;
  }
  const start = dayjs(leaseStart);
  const end = dayjs(leaseEnd);
  if (!start.isValid() || !end.isValid()) {
    return 0;
  }
  return Math.ceil(end.diff(start, 'month', true));
};

/**
 * Calculates the exact lease duration in months between two dates as a
 * fractional value, without rounding. Used by the SMB tier filter so the
 * comparison against configured tiers (3/6/12) matches the raw lease
 * duration the backend pricing service uses.
 *
 * @param leaseStart - The lease start date
 * @param leaseEnd - The lease end date
 * @returns The fractional number of months between the dates. Returns 0 if either date is invalid.
 */
export const calculateExactLeaseMonths = (leaseStart?: Date, leaseEnd?: Date): number => {
  if (!leaseStart || !leaseEnd) {
    return 0;
  }
  const start = dayjs(leaseStart);
  const end = dayjs(leaseEnd);
  if (!start.isValid() || !end.isValid()) {
    return 0;
  }
  return end.diff(start, 'month', true);
};

/**
 * Validates if the duration between start and end date meets the required exact full months (minimum).
 * Returns true if the end date is greater than or equal to the start date plus the required months.
 * Ensures exact full month duration (e.g., 01/05/2025 to 04/05/2025 = exactly 3 full months).
 *
 * @param startDate - The lease start date
 * @param endDate - The lease end date
 * @param minDurationMonths - The exact required minimum duration in months
 * @returns true if end date meets or exceeds start date + required months, false otherwise. Returns false if any date is invalid or missing.
 */
export const validateMinLeaseDuration = (
  startDate?: Date,
  endDate?: Date,
  minDurationMonths?: number,
): boolean => {
  if (!startDate || !endDate || minDurationMonths === undefined || minDurationMonths === null) {
    return false;
  }

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) {
    return false;
  }

  // Calculate the expected end date by adding exact months to start date
  const expectedEndDate = start.add(minDurationMonths, 'month');

  // Check if actual end date is greater than or equal to expected end date
  return end.isAfter(expectedEndDate, 'day') || end.isSame(expectedEndDate, 'day');
};

/**
 * Validates if the duration between start and end date does not exceed the maximum allowed months.
 * Returns true if the end date is less than or equal to the start date plus the maximum months.
 * Ensures the lease duration does not exceed the specified maximum (e.g., start + 36 months).
 *
 * @param startDate - The lease start date
 * @param endDate - The lease end date
 * @param maxDurationMonths - The maximum allowed duration in months
 * @returns true if end date is within or equal to start date + max months, false otherwise. Returns false if any date is invalid or missing.
 */
export const validateMaxLeaseDuration = (
  startDate?: Date,
  endDate?: Date,
  maxDurationMonths?: number,
): boolean => {
  if (!startDate || !endDate || maxDurationMonths === undefined || maxDurationMonths === null) {
    return false;
  }

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) {
    return false;
  }

  // Calculate the maximum allowed end date by adding max months to start date
  const maxAllowedEndDate = start.add(maxDurationMonths, 'month');

  // Check if actual end date is before or equal to max allowed end date
  return end.isBefore(maxAllowedEndDate, 'day') || end.isSame(maxAllowedEndDate, 'day');
};
